import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button, Input, Modal, toast } from '../../components/ui';
import { formatHours, formatMoney } from '../../lib/format';
import { ApiError } from '../../lib/api';
import { useDeleteClient, type ClientDeletionPreview } from './api';

/**
 * PERMANENTLY delete an archived client — and every agreement, hour, invoice
 * and payment under it. The dialog states what will be destroyed, in figures,
 * before anything can be pressed, and the button stays disabled until the
 * client's name is typed back: a keyboard reflex cannot delete the books.
 */
export function DeleteClientModal({
  open,
  onOpenChange,
  preview,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: ClientDeletionPreview;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const del = useDeleteClient(preview.id);
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const matches = norm(typed) !== '' && norm(typed) === norm(preview.name);
  const c = preview.counts;
  const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

  const lines: Array<[string, string]> = [
    [plural(c.agreements, 'agreement', 'agreements'), ''],
    [
      plural(c.timeEntries, 'time entry', 'time entries'),
      c.hours > 0 ? formatHours(c.hours, { unit: true }) : '',
    ],
    [plural(c.invoices, 'invoice', 'invoices'), ''],
    [
      plural(c.payments, 'payment', 'payments'),
      c.paymentsTotal > 0 ? formatMoney(c.paymentsTotal, { whole: true }) : '',
    ],
    [plural(c.hourBlocks, 'support hour block', 'support hour blocks'), ''],
    [plural(c.recurringInvoices, 'recurring invoice template', 'recurring invoice templates'), ''],
    [plural(c.expenses, 'expense', 'expenses') + ' on its agreements or invoices', ''],
  ];

  const close = (next: boolean) => {
    if (del.isPending) return;
    if (!next) {
      setTyped('');
      setError(null);
    }
    onOpenChange(next);
  };

  const submit = async () => {
    setError(null);
    try {
      await del.mutateAsync(typed);
      toast.success(`${preview.name} and everything under it was deleted.`);
      onDeleted();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The client could not be deleted.');
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title={`Delete ${preview.name} permanently?`}
      description="This cannot be undone. Revenue, receivables and the P&L will no longer include anything below."
      footer={
        <>
          <Button variant="outline" onClick={() => close(false)} disabled={del.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} disabled={!matches} loading={del.isPending}>
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete permanently
          </Button>
        </>
      }
    >
      <ul className="mb-4 divide-y divide-grey-100 rounded-md border border-grey-200 text-body-sm">
        {lines.map(([label, figure]) => (
          <li key={label} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
            <span className="text-secondary">{label}</span>
            <span className="num text-grey-500">{figure}</span>
          </li>
        ))}
      </ul>
      {c.forecastLinesKept > 0 && (
        <p className="mb-4 text-caption text-grey-500">
          {plural(c.forecastLinesKept, 'projected forecast line stays', 'projected forecast lines stay')} on
          the plan, with no client.
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (matches) void submit();
        }}
      >
        <Input
          label={`Type ${preview.name} to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          error={error ?? undefined}
        />
      </form>
    </Modal>
  );
}
